import React, { useEffect, useState } from "react";
import { Timeline, Card, Tag, Typography, Space, Spin, Empty, Button, Table } from "antd";
import {
  FileTextOutlined,
  InboxOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { useCompany } from "../contexts/CompanyContext";
import { useConfig } from "../contexts/ConfigContext";
import { useRegister } from "../contexts/RegisterContext";
import { useSecureRequest } from "../hooks/useSecureRequest";
import { 
  textNumberFormater, 
  formatDateTime, 
  getPaymentMethodLabel, 
  getTimelineEventInfo, 
  getAccountingEntryTypeInfo, 
  formatHumanDescription 
} from "../utils";
import dayjs from "dayjs";

const { Text } = Typography;

interface MovementTimelineViewProps {
  movementId: number | undefined;
  movementUid: string | undefined;
  initialEvents?: any[];
  refreshKey?: number;
}

export default function MovementTimelineView({
  movementId,
  movementUid,
  initialEvents = [],
  refreshKey = 0,
}: Readonly<MovementTimelineViewProps>) {
  const { company } = useCompany();
  const { config } = useConfig();
  const { register } = useRegister();
  const { securePost } = useSecureRequest();

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<any[]>(initialEvents);
  const [fulfillments, setFulfillments] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [accountingEntries, setAccountingEntries] = useState<any[]>([]);

  const fetchTimeline = async () => {
    if (!movementId && !movementUid) return;
    setLoading(true);
    try {
      const res = await securePost("/inventory/movement/get-timeline", {
        company: company.current.uid,
        document_id: movementId,
        movement_id: movementId,
        movement_uid: movementUid,
        module: config.module,
      });

      if (res.status === 200 || res.success) {
        setEvents(res.events || []);
        setFulfillments(res.fulfillments || []);
        setPayments(res.payments || []);
        setAccountingEntries(res.accounting_entries || []);
      }
    } catch (err) {
      console.error("Error fetching movement timeline:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeline();
  }, [movementId, movementUid, refreshKey]);

  // Combine and sort events
  const timelineItems = [...events].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case "ORDER_CREATED":
      case "DOCUMENT_APPROVED":
      case "CREATED":
        return <FileTextOutlined style={{ fontSize: 16, color: "#1890ff" }} />;
      case "STOCK_RECEIVED":
      case "FULL_FULFILLMENT":
        return <InboxOutlined style={{ fontSize: 16, color: "#52c41a" }} />;
      case "PARTIAL_FULFILLMENT":
        return <InboxOutlined style={{ fontSize: 16, color: "#fa8c16" }} />;
      case "PAYMENT_RECORDED":
        return <DollarOutlined style={{ fontSize: 16, color: "#faad14" }} />;
      case "DOCUMENT_SETTLED":
        return <CheckCircleOutlined style={{ fontSize: 16, color: "#722ed1" }} />;
      default:
        return <FileTextOutlined style={{ fontSize: 16, color: "#8c8c8c" }} />;
    }
  };

  return (
    <Card
      title={
        <Space>
          <BookOutlined style={{ color: "#1890ff" }} />
          <span>Trazabilidad Integral & Línea de Tiempo</span>
          <Tag color="blue">{timelineItems.length} Hitos</Tag>
        </Space>
      }
      extra={
        <Button
          type="link"
          size="small"
          icon={<ReloadOutlined />}
          onClick={fetchTimeline}
          loading={loading}
        >
          Actualizar
        </Button>
      }
      className="card-shadow"
      style={{ marginTop: 24 }}
    >
      {loading && timelineItems.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20 }}>
          <Spin tip="Cargando historial..." />
        </div>
      ) : timelineItems.length === 0 ? (
        <Empty description="No hay eventos registrados en la trazabilidad aún" />
      ) : (
        <Timeline mode="left">
          {timelineItems.map((item, idx) => {
            const dateStr = formatDateTime(item.created_at);
            const meta = item.metadata || {};
            const eventInfo = getTimelineEventInfo(item.event_type, item.title);

            return (
              <Timeline.Item
                key={item.id || idx}
                dot={getEventIcon(item.event_type)}
                color={eventInfo.color}
              >
                <div style={{ marginBottom: 4 }}>
                  <Space size="small">
                    <Text strong style={{ fontSize: 14 }}>
                      {formatHumanDescription(item.title)}
                    </Text>
                    <Tag color={eventInfo.color} style={{ fontSize: 11 }}>
                      {eventInfo.label}
                    </Tag>
                  </Space>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>{dateStr}</div>
                </div>

                <div style={{ color: "#595959", fontSize: 13, marginBottom: 6 }}>
                  {formatHumanDescription(item.description)}
                </div>

                {/* Metadata Details */}
                {item.event_type === "STOCK_RECEIVED" && meta.items_received && (
                  <div
                    style={{
                      background: "#f6ffed",
                      border: "1px solid #b7eb8f",
                      padding: "6px 12px",
                      borderRadius: 4,
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    <Text strong style={{ color: "#389e0d" }}>
                      📦 Mercancía ingresada a bodega:
                    </Text>
                    <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                      {(meta.items_received || []).map((it: any, i: number) => (
                        <li key={i}>
                          Ítem #{it.movement_item_id}: <strong>+{it.quantity_received}</strong> unid
                        </li>
                      ))}
                    </ul>
                    {meta.carrier && <div>Transportadora: {meta.carrier}</div>}
                    {meta.guide_number && <div>No. Guía: {meta.guide_number}</div>}
                    {meta.accounting_entry && (
                      <div style={{ marginTop: 2 }}>
                        <Tag color="cyan">Comprobante Bodega: {meta.accounting_entry}</Tag>
                      </div>
                    )}
                  </div>
                )}

                {item.event_type === "PAYMENT_RECORDED" && (
                  <div
                    style={{
                      background: "#fffbe6",
                      border: "1px solid #ffe58f",
                      padding: "6px 12px",
                      borderRadius: 4,
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    <div>
                      <Text strong style={{ color: "#d46b08" }}>
                        💰 Abono: {textNumberFormater(meta.amount || 0)}
                      </Text>
                    </div>
                    <div>Medio: {getPaymentMethodLabel(meta.payment_method, register.paymentOptions)}</div>
                    {meta.payment_date && <div>Fecha Pago: {dayjs(meta.payment_date).format("YYYY-MM-DD hh:mm A")}</div>}
                    {meta.reference && <div>Soporte: {meta.reference}</div>}
                    {meta.accounting_entry && (
                      <div style={{ marginTop: 2 }}>
                        <Tag color="green">Comprobante Egreso/Caja: {meta.accounting_entry}</Tag>
                      </div>
                    )}
                  </div>
                )}
              </Timeline.Item>
            );
          })}
        </Timeline>
      )}

      {/* Payments History Table */}
      {payments.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            💳 Historial de Pagos / Abonos Registrados:
          </Text>
          <Table
            dataSource={payments}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: "Fecha de Pago (Efectiva)",
                dataIndex: "payment_date",
                key: "payment_date",
                render: (d: string, record: any) => {
                  const dateVal = d || record.created_at;
                  return dateVal ? dayjs(dateVal).format("YYYY-MM-DD hh:mm A") : "-";
                },
              },
              {
                title: "Fecha de Registro (Sistema)",
                dataIndex: "created_at",
                key: "created_at",
                render: (d: string) => (d ? dayjs(d).format("YYYY-MM-DD hh:mm A") : "-"),
              },
              {
                title: "Medio de Pago",
                dataIndex: "payment_method",
                key: "payment_method",
                render: (method: string) => (
                  <Tag color="gold">{getPaymentMethodLabel(method, register.paymentOptions)}</Tag>
                ),
              },
              {
                title: "Soporte / Ref",
                dataIndex: "reference",
                key: "reference",
                render: (ref: string) => ref || "-",
              },
              {
                title: "Monto",
                dataIndex: "amount",
                key: "amount",
                align: "right",
                render: (val: number) => (
                  <Text strong style={{ color: "#52c41a" }}>
                    {textNumberFormater(val)}
                  </Text>
                ),
              },
            ]}
          />
        </div>
      )}

      {/* Accounting Entries Linked Table */}
      {accountingEntries.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Comprobantes Contables Vinculados a este Proceso:
          </Text>
          <Table
            dataSource={accountingEntries}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: "Número",
                dataIndex: "entry_number",
                key: "entry_number",
                render: (text: string) => <Tag color="blue">{text}</Tag>,
              },
              { 
                title: "Tipo", 
                dataIndex: "entry_type", 
                key: "entry_type",
                render: (type: string) => {
                  const info = getAccountingEntryTypeInfo(type);
                  return <Tag color={info.color}>{info.label}</Tag>;
                }
              },
              {
                title: "Fecha",
                dataIndex: "entry_date",
                key: "entry_date",
                render: (d: string) => (d ? new Date(d).toLocaleDateString() : "-"),
              },
              { 
                title: "Concepto", 
                dataIndex: "concept", 
                key: "concept",
                render: (text: string) => formatHumanDescription(text)
              },
              {
                title: "Débito",
                dataIndex: "total_debit",
                key: "total_debit",
                align: "right",
                render: (val) => textNumberFormater(val),
              },
              {
                title: "Crédito",
                dataIndex: "total_credit",
                key: "total_credit",
                align: "right",
                render: (val) => textNumberFormater(val),
              },
            ]}
          />
        </div>
      )}
    </Card>
  );
}
